import { Link } from '@tanstack/react-router';
import { Empty, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { day, money } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiQuery } from '../lib/query';
import type { Pilotage } from '../lib/types';

// Pilotage du cabinet (BA-093), sous les tuiles du tableau de bord et à ses filtres :
// portefeuille à échéance, taux de renouvellement, primes encaissées, contrats perdus.

const pct = (rate: number | null, language: string) =>
  rate === null ? '-' : `${(Math.round(rate * 1000) / 10).toLocaleString(language)} %`;

export function PilotageSection({
  filters,
}: {
  filters: { insurerId: string; productLineId: string; agentId: string };
}) {
  const { t, language } = useI18n();
  const query = useApiQuery<Pilotage>(
    '/v1/pilotage',
    Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
  );
  return (
    <section aria-labelledby="pilotage" className="mt-6 flex flex-col gap-4">
      <h2 id="pilotage" className="text-lg font-semibold">
        {t.pilotage.title}
      </h2>
      <QueryView query={query}>
        {(data) => (
          <>
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {data.dueWindows.map((w) => (
                <li
                  key={w.days}
                  className="rounded-xl border border-border bg-card p-4 shadow-card"
                >
                  <p className="text-sm text-muted-foreground">{t.pilotage.dueWithin(w.days)}</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">
                    {money(w.amount, language)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t.pilotage.installments(w.count)}
                  </p>
                </li>
              ))}
              <li className="rounded-xl border border-border bg-card p-4 shadow-card">
                <p className="text-sm text-muted-foreground">{t.pilotage.renewalRate}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {pct(data.renewal.rate, language)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t.pilotage.renewalDetail(data.renewal.renewed, data.renewal.notRenewed)}
                </p>
              </li>
              <li className="rounded-xl border border-border bg-card p-4 shadow-card">
                <p className="text-sm text-muted-foreground">{t.pilotage.collectedMonth}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {money(data.collected.month, language)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t.pilotage.collectedYear} : {money(data.collected.year, language)}
                </p>
              </li>
              <li className="rounded-xl border border-border bg-card p-4 shadow-card">
                <p className="text-sm text-muted-foreground">{t.pilotage.lost}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">{data.lost.count}</p>
                <p className="text-xs text-muted-foreground">
                  {data.lost.byStatus
                    .filter((s) => s.count > 0)
                    .map((s) => `${t.contractStatus[s.status]} : ${s.count}`)
                    .join(', ') || t.pilotage.since(day(data.periodFrom, language))}
                </p>
              </li>
            </ul>
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t.pilotage.byInsurer}</CardTitle>
                </CardHeader>
                {data.dueByInsurer.length === 0 ? (
                  <Empty />
                ) : (
                  <Table>
                    <THead>
                      <tr>
                        <TH>{t.portfolio.insurer}</TH>
                        <TH className="text-end">{t.pilotage.count}</TH>
                        <TH className="text-end">{t.pilotage.amount}</TH>
                      </tr>
                    </THead>
                    <TBody>
                      {data.dueByInsurer.map((r) => (
                        <TR key={r.insurerId}>
                          <TD>{r.name}</TD>
                          <TD className="text-end tabular-nums">{r.count}</TD>
                          <TD className="text-end tabular-nums">{money(r.amount, language)}</TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                )}
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t.pilotage.lostRecent}</CardTitle>
                </CardHeader>
                {data.lost.recent.length === 0 ? (
                  <CardContent>
                    <Empty>{t.pilotage.noLost}</Empty>
                  </CardContent>
                ) : (
                  <Table>
                    <THead>
                      <tr>
                        <TH>{t.installments.policy}</TH>
                        <TH>{t.installments.policyholder}</TH>
                        <TH>{t.installments.status}</TH>
                        <TH>{t.pilotage.reason}</TH>
                      </tr>
                    </THead>
                    <TBody>
                      {data.lost.recent.map((r) => (
                        <TR key={r.contractId}>
                          <TD className="whitespace-nowrap">
                            <Link
                              to="/contrats/$id"
                              params={{ id: r.contractId }}
                              className="text-primary hover:underline"
                            >
                              {r.policyNumber}
                            </Link>
                          </TD>
                          <TD>{r.policyholderName}</TD>
                          <TD>
                            <Badge tone="neutral">{t.contractStatus[r.status]}</Badge>
                            <p className="text-xs text-muted-foreground">{day(r.at, language)}</p>
                          </TD>
                          <TD className="text-sm">{r.reason ?? '-'}</TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                )}
              </Card>
            </div>
          </>
        )}
      </QueryView>
    </section>
  );
}
